import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowRight, ArrowLeft, Check, Database, Sparkles, Users as UsersIcon,
    PlayCircle, ChevronRight, Upload, BarChart3, X, LayoutDashboard,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useBranding, getStaticBranding } from "@/lib/branding";
import { useSettings, applyBranding } from "@/lib/settings-context";
import { useTheme } from "@/lib/theme-context";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import EmailOtpCard from "@/components/onboarding/EmailOtpCard";
import IndustrySampleDialog from "@/components/onboarding/IndustrySampleDialog";
import { clearOnboardingReturn } from "@/components/onboarding/OnboardingReturnBanner";

// Industry-aware sample prompts. Backend caches the full prompt list in
// /api/organizations/industries (admin payload only includes slug+label
// — we hit a richer endpoint here for prompts.)
async function loadIndustryDetail(slug) {
    try {
        const r = await api.get(`/auth/signup/industry/${slug}`);
        return r.data;
    } catch {
        return null;
    }
}

const STEPS = [
    { id: "data",      icon: Database,        label: "Bring data",       sub: "Load sample data or connect a source" },
    { id: "dashboard", icon: LayoutDashboard, label: "View dashboard",   sub: "See your dashboard come alive with data" },
    { id: "sense",     icon: Sparkles,        label: "Try Sense AI",     sub: "Ask a question. Get a chart back." },
    { id: "team",      icon: UsersIcon,       label: "Invite teammates", sub: "Bring your team into the workspace" },
];

const SAMPLE_LS_KEY = "analyzio_onboarding_sample";

export default function Onboarding() {
    const { user, refreshMe } = useAuth();
    const orgBrand = useBranding();
    const settingsCtx = useSettings();
    const themeCtx = useTheme();
    const navigate = useNavigate();
    // Onboarding always renders with the PLATFORM DEFAULT branding (the
    // branding the Super Admin saved as platform default), regardless of any
    // organisation-level override. Resolved from the public
    // `/branding/lookup` endpoint WITHOUT email/URL hints, which returns the
    // platform tenant's branding + theme mode. `undefined` = still loading,
    // `null` = lookup failed (fall back to the normal effective branding).
    const [platformBranding, setPlatformBranding] = useState(undefined);
    useEffect(() => {
        let cancelled = false;
        api.get("/branding/lookup")
            .then((r) => { if (!cancelled) setPlatformBranding(r?.data || null); })
            .catch(() => { if (!cancelled) setPlatformBranding(null); });
        return () => { cancelled = true; };
    }, []);
    // (Re-)apply the platform branding to the DOM whenever it loads and after
    // any settings refresh (focus/visibility refreshes re-apply the org
    // branding globally — this keeps onboarding on the platform default).
    const settingsSnapshot = settingsCtx?.settings;
    const setDefaultTheme = themeCtx?.setDefaultTheme;
    useEffect(() => {
        if (!platformBranding) return;
        applyBranding(platformBranding);
        if (setDefaultTheme && platformBranding.theme_mode) setDefaultTheme(platformBranding.theme_mode);
    }, [platformBranding, settingsSnapshot, setDefaultTheme]);
    // On leaving onboarding, restore the user's normal effective branding,
    // dashboard theme and theme mode via the standard settings refresh.
    const refreshSettings = settingsCtx?.refresh;
    useEffect(() => () => { if (refreshSettings) refreshSettings(); }, [refreshSettings]);
    // Header identity (logo / app name) from the platform default; falls back
    // to the regular effective branding only if the lookup failed.
    const brand = useMemo(() => {
        if (platformBranding === undefined) return { loading: true, logoUrl: null, appName: "", configuredAppName: "" };
        if (!platformBranding) return orgBrand;
        const name = (platformBranding.app_name || "").trim();
        return {
            logoUrl: platformBranding.logo_url || null,
            configuredAppName: name,
            appName: name || getStaticBranding().appName,
        };
    }, [platformBranding, orgBrand]);
    const [sp] = useSearchParams();
    const [active, setActive] = useState(0);
    const [completion, setCompletion] = useState({ percent: 0, items: [] });
    const [industryDetail, setIndustryDetail] = useState(null);
    const [tenant, setTenant] = useState(null);
    // iter-121.154 — sample-load result (dashboard + dataset ids) persisted
    // so it survives the round-trip to /dashboards + /sense-ai and back.
    const [sampleResult, setSampleResult] = useState(() => {
        try { return JSON.parse(localStorage.getItem(SAMPLE_LS_KEY) || "null"); } catch { return null; }
    });
    const [industryOpen, setIndustryOpen] = useState(false);
    // Surface the demo code that the SPA stashed during signup (when SMTP is off).
    const demoCodeHint = sp.get("demo_code") || "";

    // iter-121.154 — restore the active step when a page deep-links back
    // here via `?step=N` (guided-setup auto-return).
    useEffect(() => {
        const raw = sp.get("step");
        if (raw == null) return;
        const n = parseInt(raw, 10);
        if (!Number.isNaN(n) && n >= 0 && n < STEPS.length) setActive(n);
    }, [sp]);

    // Resolve the active tenant (for trial countdown + industry slug).
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const r = await api.get(`/organizations/${user?.org_id}/tenants`);
                const own = (r.data || []).find((t) => t.id === user?.tenant_id) || (r.data || [])[0];
                if (mounted && own) setTenant(own);
            } catch {/* ignore */}
            try {
                const c = await api.get("/profile/completion");
                if (mounted) setCompletion(c.data);
            } catch {/* ignore */}
        })();
        return () => { mounted = false; };
    }, [user]);

    // iter-121.155 — hydrate Step 2/3 from the backend so the "Bring your
    // own" path behaves like the sample path: if the tenant already has an
    // auto-instantiated dashboard (from sample OR their own connector),
    // capture its id + a dataset for the View-dashboard deep-link and the
    // Sense AI preselect. Runs once on mount; never clobbers an existing
    // sample-load result.
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const r = await api.get("/auth/signup/onboarding-status");
                const st = r.data || {};
                if (mounted && st.has_data && !sampleResult?.dashboard_id) {
                    const merged = {
                        industry: sampleResult?.industry || null,
                        dashboard_id: st.dashboard_id,
                        dataset_ids: st.dataset_ids || [],
                    };
                    setSampleResult(merged);
                    try { localStorage.setItem(SAMPLE_LS_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
                }
            } catch { /* ignore */ }
        })();
        return () => { mounted = false; };
    }, [user]);

    // Industry-specific sample prompts/dataset.
    useEffect(() => {
        if (!tenant?.industry) return;
        loadIndustryDetail(tenant.industry).then((d) => setIndustryDetail(d));
    }, [tenant?.industry]);

    const trialDaysRemaining = useMemo(() => {
        if (!tenant?.trial_ends_at) return null;
        const ms = new Date(tenant.trial_ends_at).getTime() - Date.now();
        return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    }, [tenant]);

    const refreshCompletion = async () => {
        try {
            const c = await api.get("/profile/completion");
            setCompletion(c.data);
        } catch {/* ignore */}
    };

    const onSkip = async () => {
        // iter-121.189 — leaving setup clears the persistent guided-setup strip.
        clearOnboardingReturn();
        navigate("/", { replace: true });
    };

    const next = () => setActive((i) => Math.min(STEPS.length - 1, i + 1));
    const prev = () => setActive((i) => Math.max(0, i - 1));

    const goToConnectors = () => {
        const q = new URLSearchParams({
            tab: "connectors",
            add_source: "1",
            returnTo: "/onboarding?step=1",
        });
        navigate(`/data?${q.toString()}`);
    };
    const goToUsers      = () => navigate("/users", { replace: false });

    // iter-121.154 — persist the sample-load result + advance to Step 2.
    const onSampleLoaded = (res) => {
        setSampleResult(res);
        try { localStorage.setItem(SAMPLE_LS_KEY, JSON.stringify(res)); } catch { /* ignore */ }
        refreshMe();
        refreshCompletion();
        setActive(1);
    };

    // Deep-link to the instantiated dashboard, carrying a return marker so
    // the app shows a "Back to setup" banner and lands back on the next step.
    const goToDashboard = () => {
        const did = sampleResult?.dashboard_id;
        const q = new URLSearchParams({ returnTo: "/onboarding?step=2" });
        navigate(did ? `/dashboards/${did}?${q.toString()}` : `/dashboards?${q.toString()}`);
    };

    // FIX(iter-121.187): ISSUE-1 trial onboarding — when no dashboard was
    // auto-instantiated (the platform default), send the user to the Pre-built
    // templates tab (now visible via "Available for all") so they can build a
    // dashboard from a template using the data they just loaded.
    const goToPrebuilt = () => {
        const q = new URLSearchParams({ tab: "templates", returnTo: "/onboarding?step=2" });
        navigate(`/dashboards?${q.toString()}`);
    };

    // Deep-link to Sense AI with the just-loaded dataset preselected.
    const goToSenseAI = () => {
        const ds = (sampleResult?.dataset_ids || [])[0];
        const q = new URLSearchParams({ returnTo: "/onboarding?step=3" });
        if (ds) q.set("dataset_id", ds);
        navigate(`/sense-ai?${q.toString()}`);
    };

    return (
        <div className="min-h-screen w-full bg-background text-foreground" data-testid="onboarding-page">
            {/* Top bar */}
            <header className="h-[60px] border-b border-border sticky top-0 z-10 bg-background flex items-center px-3 sm:px-6 gap-2 sm:gap-4">
                <div className="flex items-center gap-2.5 min-w-0" data-testid="onboarding-brand">
                    {brand.loading ? (
                        <div className="h-8 w-[100px]" aria-hidden="true" />
                    ) : brand.logoUrl ? (
                        <img src={brand.logoUrl} alt={brand.configuredAppName || brand.appName} className="h-8 w-auto max-w-[140px] object-contain shrink-0" />
                    ) : (
                        <div className="w-7 h-7 bg-primary flex items-center justify-center shrink-0">
                            <span className="font-mono text-primary-foreground font-semibold text-sm">{(brand.configuredAppName || brand.appName).charAt(0).toUpperCase()}</span>
                        </div>
                    )}
                    {/* iter-121.155 — show the app-name text only when the SA has
                        actually set Branding → Application name, OR when there's no
                        logo wordmark to represent the brand. Prevents a redundant/
                        fallback name printing next to a wordmark logo. */}
                    {!brand.loading && (brand.configuredAppName || !brand.logoUrl) && (
                        <span className="text-sm font-semibold tracking-tight truncate">
                            {brand.configuredAppName || brand.appName}
                        </span>
                    )}
                </div>
                <div className="flex-1" />
                {trialDaysRemaining !== null && (() => {
                    const expired = trialDaysRemaining <= 0;
                    const tone =
                        expired      ? "bg-destructive/15 text-destructive border-destructive/30 hover:bg-destructive/20" :
                        trialDaysRemaining <= 3 ? "bg-destructive/10 text-destructive border-destructive/30 hover:bg-destructive/15" :
                        trialDaysRemaining <= 7 ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30 hover:bg-amber-500/15" :
                                                  "bg-secondary text-foreground border-border hover:bg-secondary/70";
                    const dayLabel = expired ? "Expired" : `${trialDaysRemaining}d`;
                    return (
                        <button
                            type="button"
                            onClick={() => navigate("/billing")}
                            className={`h-7 sm:h-8 px-2 sm:px-2.5 flex items-center gap-1.5 border rounded-sm text-[11px] font-mono uppercase tracking-wider transition-colors shrink-0 ${tone}`}
                            title={tenant?.trial_ends_at ? `Free trial ends ${new Date(tenant.trial_ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · click to upgrade` : "Upgrade your plan"}
                            data-testid="onboarding-trial-upgrade-cta"
                            aria-label={expired ? "Trial expired — upgrade" : `Trial · ${trialDaysRemaining} days left — upgrade`}
                        >
                            <span data-testid="onboarding-trial-chip" className="flex items-center gap-1">
                                <span className="hidden sm:inline">Trial · </span>
                                <span className={expired ? "" : "text-foreground"}>{dayLabel}{!expired && <span className="hidden sm:inline"> left</span>}</span>
                            </span>
                            <span className="opacity-70">→</span>
                            <span className="font-semibold normal-case tracking-normal text-[12px]">Upgrade</span>
                        </button>
                    );
                })()}
                <Button variant="ghost" size="sm" onClick={onSkip} className="rounded-sm shrink-0 px-2 sm:px-3" data-testid="onboarding-skip-button">
                    <span className="hidden sm:inline">Skip for now</span>
                    <span className="sm:hidden">Skip</span>
                    <X className="h-3.5 w-3.5 ml-1 sm:ml-1.5" />
                </Button>
            </header>

            <div className="max-w-5xl mx-auto px-6 py-10">
                <div className="eyebrow mb-3">Welcome, {user?.name?.split(" ")[0] || "there"} 👋</div>
                <h1 className="text-4xl font-semibold tracking-tight mb-2">
                    Let's get your <span className="font-serif-display italic font-normal">{tenant?.name || "workspace"}</span> ready.
                </h1>
                <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
                    Four quick steps — and you'll have your first dashboard. You can return to any step from the profile completion chip in the top bar.
                </p>

                {/* OTP verification (Sprint 2) */}
                {user?.email && (
                    <div className="mb-6">
                        <EmailOtpCard
                            email={user.email}
                            verified={!!user.email_verified}
                            demoCodeHint={demoCodeHint}
                            onVerified={async () => { await refreshMe(); await refreshCompletion(); }}
                        />
                    </div>
                )}

                {/* Stepper */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="onboarding-stepper">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        const done = i < active;
                        const current = i === active;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => setActive(i)}
                                className={`text-left p-4 border rounded-sm transition-colors ${
                                    current ? "border-primary bg-primary/5"
                                            : done ? "border-emerald-500/40 bg-emerald-500/5"
                                                   : "border-border hover:border-foreground/30"
                                }`}
                                data-testid={`onboarding-step-${s.id}`}
                            >
                                <div className="flex items-center gap-2 mb-1.5">
                                    <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${
                                        done ? "bg-emerald-500 text-white" : current ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                                    }`}>
                                        {done ? <Check className="h-4 w-4" /> : <Icon className="h-3.5 w-3.5" />}
                                    </div>
                                    <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Step {i + 1}</span>
                                </div>
                                <div className="text-sm font-semibold">{s.label}</div>
                                <div className="text-[11px] text-muted-foreground leading-snug">{s.sub}</div>
                            </button>
                        );
                    })}
                </div>

                {/* Step body */}
                <div className="border border-border rounded-sm p-6 bg-secondary/20">
                    {active === 0 && (
                        <StepData
                            industryLabel={tenant?.industry}
                            onOpenIndustry={() => setIndustryOpen(true)}
                            onConnect={goToConnectors}
                            loaded={!!sampleResult}
                        />
                    )}
                    {active === 1 && (
                        <StepDashboard
                            sampleResult={sampleResult}
                            onOpenDashboard={goToDashboard}
                            onOpenPrebuilt={goToPrebuilt}
                            onOpenIndustry={() => setIndustryOpen(true)}
                        />
                    )}
                    {active === 2 && (
                        <StepSenseAI
                            samplePrompts={industryDetail?.sample_prompts || ["Show me the biggest numbers", "What's trending in my data?", "Build me a dashboard"]}
                            onOpenSenseAI={goToSenseAI}
                        />
                    )}
                    {active === 3 && (
                        <StepInvite onOpen={goToUsers} onFinish={() => {
                            try { localStorage.removeItem(SAMPLE_LS_KEY); } catch { /* ignore */ }
                            clearOnboardingReturn();
                            toast.success("You're all set!", { description: "Welcome to your workspace." });
                            navigate("/", { replace: true });
                        }} />
                    )}
                </div>

                {/* Footer nav */}
                <div className="flex items-center justify-between mt-6">
                    <Button variant="outline" size="sm" disabled={active === 0} onClick={prev} className="rounded-sm" data-testid="onboarding-prev">
                        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
                    </Button>
                    <div className="text-[11px] text-muted-foreground font-mono uppercase tracking-wider">
                        Profile {completion.percent}% complete
                    </div>
                    {active < STEPS.length - 1 ? (
                        <Button size="sm" onClick={next} className="rounded-sm" data-testid="onboarding-next">
                            Next <ArrowRight className="h-4 w-4 ml-1.5" />
                        </Button>
                    ) : (
                        <Button size="sm" onClick={() => { clearOnboardingReturn(); navigate("/", { replace: true }); }} className="rounded-sm" data-testid="onboarding-finish">
                            Finish <ArrowRight className="h-4 w-4 ml-1.5" />
                        </Button>
                    )}
                </div>
            </div>

            <IndustrySampleDialog
                open={industryOpen}
                onOpenChange={setIndustryOpen}
                onLoaded={onSampleLoaded}
            />
        </div>
    );
}

function StepData({ industryLabel, onOpenIndustry, onConnect, loaded }) {
    return (
        <div className="grid md:grid-cols-2 gap-4" data-testid="step-data">
            <div className="p-5 border border-border rounded-sm bg-background" data-testid="step-data-own-tile">
                <Upload className="h-5 w-5 text-primary mb-2" />
                <div className="text-base font-semibold mb-1">Bring your own</div>
                <div className="text-[12px] text-muted-foreground mb-4 leading-snug">
                    Upload a CSV/Excel, or connect to Google Ads, Meta Ads, GA4, Postgres, MySQL, S3, SFTP… 19+ connectors.
                </div>
                <Button size="sm" variant="outline" onClick={onConnect} className="rounded-sm" data-testid="step-data-open-connectors">
                    Open data sources <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
            </div>
            <div className="p-5 border border-border rounded-sm bg-background" data-testid="step-data-sample-tile">
                <PlayCircle className="h-5 w-5 text-primary mb-2" />
                <div className="text-base font-semibold mb-1">Try a sample first</div>
                <div className="text-[12px] text-muted-foreground mb-4 leading-snug">
                    Pick an industry and we'll load sample data from every connector in it — the fastest way to explore without bringing your own data yet.
                </div>
                <Button size="sm" onClick={onOpenIndustry} className="rounded-sm" data-testid="step-data-load-sample">
                    {loaded ? "Load different sample data" : "Load sample data"}
                </Button>
                {loaded && (
                    <div className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400" data-testid="step-data-loaded">
                        <Check className="h-3.5 w-3.5" /> Sample data loaded — continue to your dashboard.
                    </div>
                )}
            </div>
        </div>
    );
}

function StepDashboard({ sampleResult, onOpenDashboard, onOpenPrebuilt, onOpenIndustry }) {
    // FIX(iter-121.187): ISSUE-1 trial onboarding — the step is "ready" as soon
    // as sample data is loaded. If a dashboard was auto-instantiated we open it
    // directly; otherwise (platform default) we route to the now-visible
    // Pre-built templates so the user builds one from their loaded data. Only
    // when NOTHING has been loaded do we ask them to load sample data first.
    const hasDashboard = !!sampleResult?.dashboard_id;
    const hasData = hasDashboard
        || (sampleResult?.dataset_ids || []).length > 0
        || (sampleResult?.connectors || []).length > 0;
    return (
        <div className="space-y-4" data-testid="step-dashboard">
            <div className="flex items-start gap-3">
                <LayoutDashboard className="h-5 w-5 text-primary mt-1" />
                <div>
                    <div className="text-base font-semibold">View your dashboard</div>
                    <div className="text-[12px] text-muted-foreground leading-snug max-w-xl">
                        {hasDashboard
                            ? "We built a dashboard from the data you loaded. Open it to see your KPIs and charts populated with real rows."
                            : hasData
                                ? "Your data is loaded. Pick a ready-made template from the Pre-built section to build a dashboard on it in one click."
                                : "Load sample data in Step 1 first — then you can build a dashboard from a pre-built template here."}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-3">
                {hasDashboard ? (
                    <Button size="sm" onClick={onOpenDashboard} className="rounded-sm" data-testid="step-dashboard-open">
                        Open my dashboard <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                ) : hasData ? (
                    <Button size="sm" onClick={onOpenPrebuilt} className="rounded-sm" data-testid="step-dashboard-browse-prebuilt">
                        Browse pre-built templates <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                ) : (
                    <Button size="sm" onClick={onOpenIndustry} className="rounded-sm" data-testid="step-dashboard-load-first">
                        Load sample data first <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                )}
            </div>
        </div>
    );
}

function StepSenseAI({ samplePrompts, onOpenSenseAI }) {
    return (
        <div className="space-y-4" data-testid="step-sense-ai">
            <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-primary mt-1" />
                <div>
                    <div className="text-base font-semibold">Ask Sense AI a question</div>
                    <div className="text-[12px] text-muted-foreground leading-snug max-w-xl">
                        Skip the manual dashboard build. Sense AI grounds on your data + canonical models, returns charts and KPIs.
                    </div>
                </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
                {(samplePrompts || []).slice(0, 3).map((p, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={onOpenSenseAI}
                        className="text-left p-4 border border-border rounded-sm bg-background hover:border-primary/40 transition-colors"
                        data-testid={`sense-prompt-${i}`}
                    >
                        <BarChart3 className="h-4 w-4 text-primary mb-2" />
                        <div className="text-[13px] leading-snug">{p}</div>
                    </button>
                ))}
            </div>
            <div>
                <Button size="sm" onClick={onOpenSenseAI} className="rounded-sm" data-testid="step-sense-open">
                    Open Sense AI <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
            </div>
        </div>
    );
}

function StepInvite({ onOpen, onFinish }) {
    return (
        <div className="space-y-4" data-testid="step-invite">
            <div className="flex items-start gap-3">
                <UsersIcon className="h-5 w-5 text-primary mt-1" />
                <div>
                    <div className="text-base font-semibold">Bring your team</div>
                    <div className="text-[12px] text-muted-foreground leading-snug max-w-xl">
                        Invite collaborators with role-based access. Org Admins see everything; Analysts can ask questions; Viewers see shared dashboards.
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-3">
                <Button size="sm" onClick={onOpen} className="rounded-sm" data-testid="step-invite-open">
                    Invite teammates
                </Button>
                <Button size="sm" variant="outline" onClick={onFinish} className="rounded-sm" data-testid="step-invite-skip">
                    Maybe later
                </Button>
            </div>
        </div>
    );
}
